// --- PMT helper (Excel-compatible) ---
// Returns a POSITIVE payment amount (good for UI).
// rate  = interest per period (e.g., annual/12 for monthly)
// nper  = number of periods
// pv    = present value (loan principal financed)
// fv    = future value (default 0)
// type  = 0 = end of period, 1 = beginning of period
function PMT(rate, nper, pv, fv = 0, type = 0) {
  if (rate === 0) {
    // Even with type=1, when rate=0 the payment is a straight line
    return Math.abs((pv + fv) / nper)
  }
  const pow = Math.pow(1 + rate, nper)
  // Excel formula rearranged to return a positive outflow
  const pmt = (rate * (pv * pow + fv)) / ((1 + rate * type) * (pow - 1))
  return Math.abs(pmt)
}

// Helper to get min and max from options with fallback to backend parameters
export const getMinMaxRates = (options, lowestInterestRate = null, highestInterestRate = null) => {
  // Priority 1: Use backend parameters if defined
  if (typeof lowestInterestRate === 'number' && typeof highestInterestRate === 'number') {
    return { min: lowestInterestRate, max: highestInterestRate }
  }
  
  // Priority 2: Use loanPurposes min/max if available
  if (Array.isArray(options) && options.length > 0) {
    let min = options[0].Lowest
    let max = options[0].Highest
    options.forEach(opt => {
      if (typeof opt.Lowest === 'number' && opt.Lowest < min) min = opt.Lowest
      if (typeof opt.Highest === 'number' && opt.Highest > max) max = opt.Highest
    })
    return { min, max: max || 30 } // Default max to 30 if it ends up as 0
  }
  
  // Priority 3: Default fallback
  return { min: 0, max: 30 }
}

export const formatCurrency = (amount) => {
  if (isNaN(amount) || amount === null || amount === undefined) return '$0.00'
  return new Intl.NumberFormat('en-NZ', {
    style: 'currency', 
    currency: 'NZD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(Number(amount))
}

// Optional: small rounding utility for display
const round2 = (n) => Number((Math.round((n + Number.EPSILON) * 100) / 100).toFixed(2))

/**
 * Calculate repayments using PMT and return a rich breakdown
 * 
 * @param {object} opts
 * @param {number} opts.loanAmount                  - principal (without one-time fees)
 * @param {number} opts.repaymentInMonths           - loan term in months
 * @param {number} opts.interestRate                - annual interest rate in % (e.g., 10 for 10%)
 * @param {number} [opts.loanEstablishmentFee=0]    - one-time fee added to principal
 * @param {number} [opts.PPSRfee=0]                 - one-time fee added to principal
 * @param {number} [opts.balloonPaymentAmount=0]    - balloon payment at end of loan
 * @param {number} [opts.depositAmount=0]           - deposit amount (reduces loan amount)
 * @param {number} [opts.maintenanceFee=0.5]        - weekly maintenance fee ($/week)
 * @param {'weekly'|'fortnightly'|'monthly'} opts.repaymentFrequency
 * @param {0|1}   [opts.paymentTiming=0]            - 0 end of period, 1 beginning (Excel's "type")
 */
export const calculateRepayment = ({
  loanAmount,
  repaymentInMonths,
  interestRate, // annual, in %
  loanEstablishmentFee = 0,
  PPSRfee = 0,
  balloonPaymentAmount = 0,
  depositAmount = 0,
  maintenanceFee = 0.5, // weekly fee, independent of repayment frequency
  repaymentFrequency, // 'weekly' | 'fortnightly' | 'monthly'
  paymentTiming = 0,   // Excel "type": 0=end, 1=begin
}) => {
  // 1) Payments per year by frequency
  const PAYS_PER_YEAR = {
    weekly: 52,
    fortnightly: 26,
    monthly: 12,
  }
  const paymentsPerYear = PAYS_PER_YEAR[repaymentFrequency]
  if (!paymentsPerYear) {
    throw new Error("Invalid repayment frequency. Use 'weekly', 'fortnightly', or 'monthly'.")
  }

  // 2) Amount actually financed includes one-time fees added to principal, minus deposit
  const totalOneTimeFees = (loanEstablishmentFee || 0) + (PPSRfee || 0)
  const deposit = depositAmount || 0
  // The amount that needs to be financed is the loan amount plus fees, minus any deposit
  const principalFinanced = loanAmount + totalOneTimeFees - deposit

  // 3) Periodic rate and number of payments
  const annualRate = (interestRate || 0) / 100
  const periodicRate = annualRate / paymentsPerYear
  let totalPayments = (repaymentInMonths / 12) * paymentsPerYear
  if (balloonPaymentAmount > 0) {
    totalPayments = totalPayments -1
  }

  // 4) Weekly maintenance fee handling
  //    Convert weekly maintenance to the chosen repayment frequency
  const weeklyFee = maintenanceFee || 0
  // To simplify rounding for the yearly it should be 4, instead of 4.33
  const periodicMaintenance = paymentsPerYear === 12 ? (weeklyFee * 4) : (weeklyFee * (52 / paymentsPerYear))
  const numberOfWeeksInTerm = (repaymentInMonths / 12) * 52
  const totalMaintenanceFee = weeklyFee * numberOfWeeksInTerm

  // 5) Loan-only payment using PMT (no maintenance)
  const periodicRepaymentLoanOnly = PMT(
    periodicRate,
    totalPayments,
    principalFinanced,
    -balloonPaymentAmount, // reduce periodic payments by having a balloon at end
    paymentTiming
  )

  // 6) Full payment including maintenance
  const periodicRepaymentWithMaintenance = periodicRepaymentLoanOnly + periodicMaintenance

  // 7) Totals (loan-only vs with maintenance)
  const totalRepaidLoanOnly = periodicRepaymentLoanOnly * totalPayments + balloonPaymentAmount
  const totalInterest = totalRepaidLoanOnly - principalFinanced

  const totalRepaidWithMaintenance = periodicRepaymentWithMaintenance * totalPayments + balloonPaymentAmount

  // 8) Cost of borrowing = interest + one-time fees + maintenance
  const totalCostOfBorrowing = totalInterest + totalOneTimeFees + totalMaintenanceFee

  // 9) Return a display-friendly object (include both raw + rounded where helpful)
  return {
    // Inputs echoed
    inputs: {
      loanAmount,
      repaymentInMonths,
      interestRatePercent: interestRate,
      repaymentFrequency,
      paymentTiming, // 0 end / 1 begin
      fees: {
        loanEstablishmentFee,
        PPSRfee,
        weeklyMaintenanceFee: weeklyFee,
      },
      balloonPaymentAmount,
      depositAmount: deposit
    },

    // Derived rates & counts
    paymentsPerYear,
    totalPayments,
    periodicRate,      // e.g., weekly/monthly rate (decimal)
    annualRate,        // decimal

    // Principal & fees
    principalFinanced,
    totalOneTimeFees,
    totalMaintenanceFee,

    // Payments (raw)
    periodicRepaymentLoanOnly,
    periodicMaintenance,
    periodicRepaymentWithMaintenance,

    // Payments (rounded for UI)
    display: {
      periodicRepaymentLoanOnly: round2(periodicRepaymentLoanOnly),
      periodicMaintenance: round2(periodicMaintenance),
      periodicRepaymentWithMaintenance: round2(periodicRepaymentWithMaintenance),
      totalRepaidLoanOnly: round2(totalRepaidLoanOnly),
      totalRepaidWithMaintenance: round2(totalRepaidWithMaintenance),
      totalInterest: round2(totalInterest),
      totalOneTimeFees: round2(totalOneTimeFees),
      totalMaintenanceFee: round2(totalMaintenanceFee),
      totalCostOfBorrowing: round2(totalCostOfBorrowing),
      balloonPaymentAmount: round2(balloonPaymentAmount),
      depositAmount: round2(deposit)
    },

    // Totals (raw)
    totals: {
      totalRepaidLoanOnly,
      totalRepaidWithMaintenance,
      totalInterest,
      totalCostOfBorrowing,
    }
  }
}

// --- PV helper (Excel-compatible, returns POSITIVE PV for POSITIVE pmt) ---
// rate  = interest per period (e.g., annual/12 for monthly)
// nper  = number of periods
// pmt   = periodic payment amount (excluding per-period maintenance)
// fv    = future value (default 0)
// type  = 0 = end of period, 1 = beginning of period
function PV(rate, nper, pmt, fv = 0, type = 0) {
  if (rate === 0) {
    // Linear case; timing doesn't affect PV when rate = 0
    return Math.max(0, Math.abs(pmt) * nper - fv);
  }
  const A = Math.pow(1 + rate, nper);
  const pv = (((1 + rate * type) * Math.abs(pmt) * (A - 1)) / rate - fv) / A;
  return Math.max(0, pv);
}

/**
 * Given an affordable periodic payment, compute the max loan you can borrow.
 *
 * @param {object} opts
 * @param {number} opts.repaymentInMonths             - loan term in months
 * @param {number} opts.interestRate                  - annual interest rate in % (e.g., 10 for 10%)
 * @param {number} [opts.loanEstablishmentFee=0]      - one-time fee added to principal
 * @param {number} [opts.PPSRfee=0]                   - one-time fee added to principal
 * @param {number} [opts.maintenanceFee=0.5]          - weekly maintenance fee ($/week)
 * @param {'weekly'|'fortnightly'|'monthly'} opts.repaymentFrequency
 * @param {number} opts.affordablePerPeriod           - how much the borrower can pay per chosen period (e.g., $60 weekly)
 * @param {0|1}   [opts.paymentTiming=0]              - 0 end of period, 1 beginning (Excel "type")
 */
export const calculateMaxBorrowable = ({
  repaymentInMonths,
  interestRate,
  loanEstablishmentFee = 0,
  PPSRfee = 0,
  maintenanceFee = 0.5, // weekly fee
  repaymentFrequency, // 'weekly' | 'fortnightly' | 'monthly'
  affordablePerPeriod,
  paymentTiming = 0
}) => {
  const PAYS_PER_YEAR = { weekly: 52, fortnightly: 26, monthly: 12 }
  const paymentsPerYear = PAYS_PER_YEAR[repaymentFrequency]
  if (!paymentsPerYear) {
    throw new Error("Invalid repayment frequency. Use 'weekly', 'fortnightly', or 'monthly'.")
  }
  if (affordablePerPeriod <= 0) {
    return { loanAmount: 0, errorReason: 'Affordable payment must be > 0' }
  }

  // Rates & counts
  const annualRate = (interestRate || 0) / 100
  const periodicRate = annualRate / paymentsPerYear
  const totalPayments = (repaymentInMonths / 12) * paymentsPerYear

  // Fees
  const totalOneTimeFees = (loanEstablishmentFee || 0) + (PPSRfee || 0)

  // Convert weekly maintenance to chosen repayment period
  const weeklyFee = maintenanceFee || 0
  const periodicMaintenance = weeklyFee * (52 / paymentsPerYear)

  // Make sure the affordable payment actually covers maintenance first
  const loanPortionPerPeriod = affordablePerPeriod - periodicMaintenance
  if (loanPortionPerPeriod <= 0) {
    return {
      loanAmount: 0,
      errorReason: 'Affordable payment does not cover maintenance fee for this frequency.',
      details: { affordablePerPeriod, periodicMaintenance: round2(periodicMaintenance) }
    }
  }

  // Present value of the loan-only payment stream (this is principal FINANCED)
  const principalFinanced = PV(periodicRate, totalPayments, loanPortionPerPeriod, 0, paymentTiming)

  // Cash loan amount is financed principal minus one-time fees (which are added to principal)
  const loanAmount = Math.max(0, principalFinanced - totalOneTimeFees)

  // Useful totals for display (optional)
  const totalMaintenanceWeeks = (repaymentInMonths / 12) * 52
  const totalMaintenanceFee = weeklyFee * totalMaintenanceWeeks

  return {
    inputs: {
      repaymentInMonths,
      interestRatePercent: interestRate,
      repaymentFrequency,
      affordablePerPeriod,
      paymentTiming,
      fees: {
        loanEstablishmentFee,
        PPSRfee,
        weeklyMaintenanceFee: weeklyFee
      }
    },
    paymentsPerYear,
    totalPayments,
    periodicRate,
    principalFinanced, // includes one-time fees rolled in
    loanAmount,        // cash to borrower (ex-fees)
    display: {
      periodicMaintenance: round2(periodicMaintenance),
      principalFinanced: round2(principalFinanced),
      loanAmount: round2(loanAmount),
      totalOneTimeFees: round2(totalOneTimeFees),
      totalMaintenanceFee: round2(totalMaintenanceFee)
    }
  }
}
